<template>
  <p id="addLinkAttach">{{ $t("节点配置") }}</p>
  <t-form-item
    name="type"
    :rules="requireRule"
    :required-mark="false"
    style="margin-bottom: 10px"
  >
    <template #label>
      <span style="color: #6d778c">{{ $t("消息内容") }}</span>
    </template>
    <t-space>
      <t-radio-group :value="formData.type" @change="onTypeChange">
        <t-radio value="1">{{ $t("纯文本") }}</t-radio>
        <t-radio value="2">{{ $t("富文本") }}</t-radio>
      </t-radio-group>
    </t-space>
  </t-form-item>
  <t-form-item
    name="content"
    :required-mark="false"
    :rules="[
      {
        validator: (_str) => {
          const str =
            formData.type === '2'
              ? turndownService.turndown(trim(_str))
              : trim(_str);
          return !!str;
        },
        message: $t('不能为空'),
        trigger: 'change',
      },
      {
        validator: (_str) => {
          const str =
            formData.type === '2'
              ? turndownService.turndown(trim(_str))
              : trim(_str);
          return get(str, 'length', 0) <= (maxLimits?.maxAnswerLength ?? 2000);
        },
        message: t('不超过{maxLength}字符', {
          maxLength: maxLimits?.maxAnswerLength ?? 2000,
        }),
        trigger: 'change',
      },
    ]"
  >
    <t-textarea
      v-if="formData.type === '1'"
      v-model="formData.content"
      :autosize="{ minRows: 5 }"
      :maxlength="maxLimits?.maxAnswerLength ?? 2000"
      :placeholder="$t('请输入')"
    />
    <rich-text-editor
      v-if="formData.type === '2'"
      :value="formData.content"
      @updateContent="updateContent"
    />
  </t-form-item>
</template>

<script lang="ts" setup>
import { inject, reactive, ref, Ref, watch } from "vue";
import { trim, get } from "lodash-es";
import turndownService from "@/utils/turndownService";
import { getHtml } from "@/utils/markedUtil";
import { t } from "@/utils/i18n";

const emit = defineEmits(["updateData"]);

const maxLimits = inject<Ref<{ maxAnswerLength: number }>>("maxLimits");

type Model = {
  nodeData: {
    type: "1" | "2";
    content: string;
  };
};

const props = defineProps<{
  data: Model["nodeData"];
}>();

const formData = reactive<Model["nodeData"]>({
  type: props.data.type ?? "1",
  content:
    props.data.type === "2"
      ? (getHtml(props.data.content) as string)
      : (props.data.content ?? ""),
});

// 切换类型时各自缓存内容，避免丢失
// 富文本内容用 savedRichContentHtml 缓存 HTML，保证切回来编辑器能正确渲染
const savedTextContent = ref(formData.type === "1" ? formData.content : "");
const savedRichContentHtml = ref(formData.type === "2" ? formData.content : "");

const onTypeChange = (v) => {
  if (formData.type === "1") {
    savedTextContent.value = formData.content;
    formData.content = savedRichContentHtml.value;
  } else {
    // 离开富文本：把当前 Markdown 转成 HTML 缓存，供下次切回时编辑器使用
    savedRichContentHtml.value = getHtml(formData.content) as string;
    formData.content = savedTextContent.value;
  }
  formData.type = v;
};

const updateContent = (v) => {
  formData.content = v;
};

const requireRule = [
  {
    validator: (str) => {
      return !!trim(str);
    },
    message: t("不能为空"),
    trigger: "change",
  },
];

watch(formData, (newVal, oldVal) => {
  emit("updateData", newVal);
});
</script>
<style lang="less" scoped></style>
